<template>
  <a-config-provider :locale="zhCN">
    <div class="employee-coupon-manage">
      <div class="page-content">
        <div class="page-header">
          <button class="page-back-btn" @click="router.push('/')">
            <ArrowLeftOutlined />
          </button>
          <span class="page-title">员工餐券管理</span>
        </div>

        <a-card :bordered="false">
          <div class="search-form">
            <div class="query-field">
              <div class="mb-1 text-sm text-slate-600">员工工号</div>
              <a-input
                v-model:value="employeeIdInput"
                allow-clear
                inputmode="numeric"
                placeholder="请输入员工工号"
                @press-enter="handleSearch"
              />
            </div>
            <div class="query-field">
              <div class="mb-1 text-sm text-slate-600">员工姓名</div>
              <a-input
                v-model:value="employeeNameInput"
                allow-clear
                placeholder="请输入员工姓名"
                @press-enter="handleSearch"
              />
            </div>
            <div class="search-actions">
              <a-button type="primary" :loading="employeeLoading" @click="handleSearch">
                <SearchOutlined />查询
              </a-button>
              <a-button @click="handleReset"><ReloadOutlined />重置</a-button>
            </div>
          </div>

          <div class="action-toolbar">
            <div class="toolbar-description">查询员工餐券余额、使用流水并进行人工调整</div>
            <div class="toolbar-actions">
              <a-button type="primary" @click="openEmployeeForm()">新增员工</a-button>
              <a-button :loading="exportLoading" @click="exportAllEmployees">
                <DownloadOutlined />全量导出 Excel
              </a-button>
              <a-button @click="downloadImportTemplate">
                <DownloadOutlined />下载导入模板
              </a-button>
              <a-button @click="openImportFilePicker('add')"> <UploadOutlined />批量增加 </a-button>
              <a-button danger @click="openImportFilePicker('reduce')">
                <UploadOutlined />批量扣减
              </a-button>
            </div>
            <input
              ref="importFileInput"
              class="hidden"
              type="file"
              accept=".xlsx,.xls"
              @change="handleImportFileChange"
            />
          </div>

          <a-table
            row-key="customerId"
            :columns="employeeColumns"
            :data-source="employees"
            :loading="employeeLoading"
            :pagination="employeePagination"
            :scroll="{ x: 900 }"
            @change="handleEmployeeTableChange"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'foodTicketAccount'">
                <span class="coupon-balance">{{ formatQuantity(record.foodTicketAccount) }}</span>
              </template>
              <template v-else-if="column.key === 'wechatBinding'">
                <a-tag :color="record.openId?.trim() ? 'green' : 'default'">
                  {{ record.openId?.trim() ? '已绑定微信' : '未绑定' }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'status'">
                <a-tag :color="record.status === 0 && record.disabled === 0 ? 'green' : 'default'">
                  {{ record.status === 0 && record.disabled === 0 ? '正常' : '不可用' }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'action'">
                <a-button type="link" size="small" @click="openEmployeeForm(record)">
                  修改
                </a-button>
                <a-button type="link" size="small" @click="openEmployeeDetail(record)">
                  查看与调整
                </a-button>
                <a-button
                  type="link"
                  danger
                  size="small"
                  :loading="deletingEmployeeId === record.id"
                  :disabled="deletingEmployeeId !== undefined"
                  @click="confirmDeleteEmployee(record)"
                >
                  删除
                </a-button>
              </template>
            </template>
            <template #emptyText>暂无员工数据</template>
          </a-table>
        </a-card>
      </div>

      <a-modal
        v-model:open="employeeFormOpen"
        :title="editingEmployee ? '修改员工' : '新增员工'"
        :confirm-loading="employeeSaving"
        :closable="!employeeSaving"
        :mask-closable="!employeeSaving"
        :keyboard="!employeeSaving"
        :cancel-button-props="{ disabled: employeeSaving }"
        :width="520"
        ok-text="保存"
        cancel-text="取消"
        @ok="submitEmployee"
      >
        <a-form layout="vertical" :disabled="employeeSaving">
          <a-form-item label="员工工号" required>
            <a-input
              v-model:value="employeeForm.customerId"
              :readonly="!!editingEmployee"
              inputmode="numeric"
              placeholder="请输入员工工号"
            />
          </a-form-item>
          <a-form-item label="员工姓名" required>
            <a-input
              v-model:value="employeeForm.customerName"
              :readonly="!!editingEmployee"
              placeholder="请输入员工姓名"
            />
          </a-form-item>
          <a-form-item label="员工信息">
            <a-textarea
              v-model:value="employeeForm.customerInfo"
              :rows="3"
              placeholder="请输入员工信息"
            />
          </a-form-item>
          <a-form-item label="备注">
            <a-textarea v-model:value="employeeForm.remark" :rows="3" placeholder="请输入备注" />
          </a-form-item>
        </a-form>
      </a-modal>

      <a-drawer
        v-model:open="drawerOpen"
        title="员工餐券详情"
        placement="right"
        :width="drawerWidth"
        destroy-on-close
      >
        <template v-if="selectedEmployee">
          <div class="mb-4 rounded-xl bg-amber-50 p-4">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div class="text-lg font-bold text-slate-900">{{
                  selectedEmployee.customerName
                }}</div>
                <div class="text-sm text-slate-500">工号：{{ selectedEmployee.customerId }}</div>
              </div>
              <div class="text-right">
                <div class="text-xs text-slate-500">当前餐券余额</div>
                <div class="text-3xl font-bold text-amber-600">
                  {{ formatQuantity(selectedEmployee.foodTicketAccount) }}
                </div>
              </div>
            </div>
            <div class="mt-4 flex gap-2">
              <a-button type="primary" class="flex-1" @click="openAdjustment('add')"
                >增加餐券</a-button
              >
              <a-button danger class="flex-1" @click="openAdjustment('reduce')">扣减餐券</a-button>
            </div>
          </div>

          <div class="mb-3 flex flex-wrap items-end gap-2">
            <div>
              <div class="mb-1 text-sm text-slate-600">流水日期</div>
              <a-range-picker v-model:value="recordDateRange" value-format="YYYY-MM-DD" />
            </div>
            <div>
              <div class="mb-1 text-sm text-slate-600">变动方向</div>
              <a-select v-model:value="recordDirect" class="w-28" allow-clear placeholder="全部">
                <a-select-option :value="1">增加</a-select-option>
                <a-select-option :value="2">减少</a-select-option>
              </a-select>
            </div>
            <a-button type="primary" @click="searchRecords">查询流水</a-button>
            <a-button @click="resetRecordFilters">重置</a-button>
          </div>

          <a-table
            row-key="customerAccountRecordId"
            :columns="recordColumns"
            :data-source="records"
            :loading="recordLoading"
            :pagination="recordPagination"
            :scroll="{ x: 1250 }"
            size="small"
            @change="handleRecordTableChange"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'direct'">
                <a-tag :color="record.direct === 1 ? 'green' : 'red'">
                  {{ record.direct === 1 ? '增加' : '减少' }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'accountQuantity'">
                <span :class="record.direct === 1 ? 'text-green-600' : 'text-red-600'">
                  {{ record.direct === 1 ? '+' : '-' }}{{ formatQuantity(record.accountQuantity) }}
                </span>
              </template>
              <template v-else-if="column.key === 'afterAccountQuantity'">
                {{ formatQuantity(calculateAfterQuantity(record)) }}
              </template>
              <template v-else-if="column.key === 'source'">
                {{ record.sourceTypeName || '-' }}
                <span v-if="record.sourceCode" class="text-slate-400"
                  >（{{ record.sourceCode }}）</span
                >
              </template>
            </template>
            <template #emptyText>暂无餐券流水</template>
          </a-table>
        </template>
      </a-drawer>

      <a-modal
        v-model:open="adjustmentOpen"
        :title="adjustmentType === 'add' ? '增加餐券' : '扣减餐券'"
        :confirm-loading="adjustmentLoading"
        ok-text="确认提交"
        cancel-text="取消"
        @ok="submitAdjustment"
      >
        <a-alert
          class="mb-4"
          type="info"
          show-icon
          :message="`${selectedEmployee?.customerName || ''}（工号 ${
            selectedEmployee?.customerId || ''
          }）`"
          :description="`当前余额：${formatQuantity(selectedEmployee?.foodTicketAccount || 0)}`"
        />
        <div class="mb-4">
          <div class="mb-1 text-sm font-medium">调整数量</div>
          <a-input-number
            v-model:value="adjustmentQuantity"
            class="w-full"
            :min="0"
            :precision="2"
            placeholder="请输入大于 0 的数量"
          />
        </div>
        <div>
          <div class="mb-1 text-sm font-medium">调整原因</div>
          <a-textarea
            v-model:value="adjustmentRemark"
            :maxlength="200"
            show-count
            :rows="3"
            placeholder="请输入调整原因（必填）"
          />
        </div>
      </a-modal>

      <a-modal
        v-model:open="importModalOpen"
        :title="importType === 'add' ? '批量增加餐券' : '批量扣减餐券'"
        :confirm-loading="importSubmitting"
        :ok-button-props="{ disabled: importErrors.length > 0 || importRows.length === 0 }"
        ok-text="确认导入"
        cancel-text="取消"
        @ok="submitBatchImport"
        @cancel="resetImportState"
      >
        <a-alert
          class="mb-3"
          :type="importErrors.length > 0 ? 'error' : 'success'"
          show-icon
          :message="
            importErrors.length > 0
              ? `发现 ${importErrors.length} 个问题，请修正后重新选择文件`
              : `校验通过，共 ${importRows.length} 名员工`
          "
        />
        <div class="mb-3 text-sm text-slate-600">
          文件：{{ importFileName || '-' }}。表头必须包含“员工工号、员工姓名、餐券数量、调整原因”。
        </div>
        <div v-if="importErrors.length" class="max-h-64 overflow-y-auto rounded bg-red-50 p-3">
          <div v-for="error in importErrors" :key="error" class="mb-1 text-sm text-red-600">
            {{ error }}
          </div>
        </div>
        <a-table
          v-else
          row-key="customerId"
          :columns="importPreviewColumns"
          :data-source="importRows"
          :pagination="{ pageSize: 5, hideOnSinglePage: true }"
          size="small"
          :scroll="{ x: 560 }"
        />
      </a-modal>
    </div>
  </a-config-provider>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { useRouter } from 'vue-router';
  import {
    ArrowLeftOutlined,
    DownloadOutlined,
    ReloadOutlined,
    SearchOutlined,
    UploadOutlined,
  } from '@ant-design/icons-vue';
  import { Modal, message } from 'ant-design-vue';
  import type { TablePaginationConfig } from 'ant-design-vue';
  import zhCN from 'ant-design-vue/es/locale/zh_CN';
  import dayjs from 'dayjs';
  import * as XLSX from 'xlsx';
  import type {
    CustomerAccountAdjustmentDetailModel,
    CustomerAccountRecordModel,
    CustomerAccountRecordSelectRequestModel,
    CustomerModel,
    CustomerSelectRequestModel,
    EmployeeFormModel,
  } from '@/model/customer';
  import {
    addCustomer,
    addCustomerFoodTicket,
    deleteCustomer,
    editCustomer,
    forbidCustomer,
    getCustomerAccountRecordList,
    getCustomerList,
    reduceCustomerFoodTicket,
  } from '@/service/customer';

  const router = useRouter();
  const employeeFormOpen = ref(false);
  const employeeSaving = ref(false);
  const deletingEmployeeId = ref<number>();
  const editingEmployee = ref<CustomerModel>();
  const employeeForm = ref<EmployeeFormModel>({
    customerId: '',
    customerName: '',
    customerInfo: '',
    remark: '',
  });
  const employeeLoading = ref(false);
  const employees = ref<CustomerModel[]>([]);
  const employeeIdInput = ref('');
  const employeeNameInput = ref('');
  const employeePage = ref(1);
  const employeePageSize = ref(10);
  const employeeTotal = ref(0);
  const drawerOpen = ref(false);
  const selectedEmployee = ref<CustomerModel>();
  const recordLoading = ref(false);
  const records = ref<CustomerAccountRecordModel[]>([]);
  const recordDateRange = ref<string[]>();
  const recordDirect = ref<1 | 2>();
  const recordPage = ref(1);
  const recordPageSize = ref(10);
  const recordTotal = ref(0);
  const adjustmentOpen = ref(false);
  const adjustmentLoading = ref(false);
  const adjustmentType = ref<'add' | 'reduce'>('add');
  const adjustmentQuantity = ref<number>();
  const adjustmentRemark = ref('');
  const exportLoading = ref(false);
  const importFileInput = ref<HTMLInputElement>();
  const importType = ref<'add' | 'reduce'>('add');
  const importModalOpen = ref(false);
  const importSubmitting = ref(false);
  const importFileName = ref('');
  const importRows = ref<CustomerAccountAdjustmentDetailModel[]>([]);
  const importErrors = ref<string[]>([]);

  interface CouponImportExcelRow {
    员工工号?: unknown;
    员工姓名?: unknown;
    餐券数量?: unknown;
    调整原因?: unknown;
  }

  const drawerWidth = computed(() => (window.innerWidth < 768 ? '100%' : 760));
  const employeePagination = computed<TablePaginationConfig>(() => ({
    current: employeePage.value,
    pageSize: employeePageSize.value,
    total: employeeTotal.value,
    showSizeChanger: true,
    showTotal: (total: number) => `共 ${total} 名员工`,
  }));
  const recordPagination = computed<TablePaginationConfig>(() => ({
    current: recordPage.value,
    pageSize: recordPageSize.value,
    total: recordTotal.value,
    showSizeChanger: true,
    showTotal: (total: number) => `共 ${total} 条流水`,
  }));

  const employeeColumns = [
    { title: '员工工号', dataIndex: 'customerId', key: 'customerId', width: 120 },
    { title: '员工姓名', dataIndex: 'customerName', key: 'customerName', width: 160 },
    { title: '餐券余额', dataIndex: 'foodTicketAccount', key: 'foodTicketAccount', width: 120 },
    { title: '微信绑定', key: 'wechatBinding', width: 140 },
    { title: '状态', key: 'status', width: 100 },
    { title: '操作', key: 'action', width: 260, fixed: 'right' },
  ];
  const recordColumns = [
    { title: '时间', dataIndex: 'createTime', key: 'createTime', width: 170, fixed: 'left' },
    { title: '方向', key: 'direct', width: 80 },
    { title: '变化数量', key: 'accountQuantity', width: 110 },
    {
      title: '变化前',
      dataIndex: 'beforeAccountQuantity',
      key: 'beforeAccountQuantity',
      width: 90,
    },
    { title: '变化后', key: 'afterAccountQuantity', width: 90 },
    { title: '餐厅', dataIndex: 'shopName', key: 'shopName', width: 140 },
    { title: '来源', key: 'source', width: 200 },
    { title: '来源备注', dataIndex: 'sourceRemark', key: 'sourceRemark', width: 200 },
    { title: '操作人', dataIndex: 'createBy', key: 'createBy', width: 120 },
  ];
  const importPreviewColumns = [
    { title: '员工工号', dataIndex: 'customerId', key: 'customerId', width: 110 },
    { title: '员工姓名', dataIndex: 'customerName', key: 'customerName', width: 120 },
    { title: '餐券数量', dataIndex: 'accountQuantity', key: 'accountQuantity', width: 100 },
    { title: '调整原因', dataIndex: 'sourceRemark', key: 'sourceRemark', width: 220 },
  ];

  const formatQuantity = (value: number): string =>
    Number(value || 0).toLocaleString('zh-CN', { maximumFractionDigits: 2 });

  const calculateAfterQuantity = (record: CustomerAccountRecordModel): number =>
    record.direct === 1
      ? record.beforeAccountQuantity + record.accountQuantity
      : record.beforeAccountQuantity - record.accountQuantity;

  const loadEmployees = async (): Promise<void> => {
    const customerIdText = employeeIdInput.value.trim();
    if (customerIdText && !/^\d+$/.test(customerIdText)) {
      message.warning('员工工号只能输入数字');
      return;
    }
    const request: CustomerSelectRequestModel = {
      pageNum: employeePage.value,
      pageSize: employeePageSize.value,
      data: {
        type: 1,
        status: 0,
        customerId: customerIdText ? Number(customerIdText) : undefined,
        customerName: employeeNameInput.value.trim() || undefined,
      },
    };
    employeeLoading.value = true;
    try {
      const response = await getCustomerList(request);
      if (response.code !== 200) {
        message.error(response.msg || '员工列表查询失败');
        return;
      }
      const availableEmployees = (response.result?.list || []).filter(
        (item) => item.disabled === 0,
      );
      employees.value = availableEmployees;
      employeeTotal.value = response.result?.total || 0;
    } catch (error) {
      console.error('员工列表查询失败:', error);
      message.error('员工列表查询失败，请稍后重试');
    } finally {
      employeeLoading.value = false;
    }
  };

  const openEmployeeForm = (employee?: CustomerModel): void => {
    if (employeeSaving.value) return;
    editingEmployee.value = employee ? { ...employee } : undefined;
    employeeForm.value = {
      customerId: employee ? String(employee.customerId) : '',
      customerName: employee?.customerName || '',
      customerInfo: employee?.customerInfo ?? '',
      remark: employee?.remark ?? '',
    };
    employeeFormOpen.value = true;
  };

  const submitEmployee = async (): Promise<void> => {
    if (employeeSaving.value) return;
    const employee = editingEmployee.value;
    const customerIdText = employeeForm.value.customerId.trim();
    const customerId = Number(customerIdText);
    const customerName = employeeForm.value.customerName.trim();
    if (!/^\d+$/.test(customerIdText) || !Number.isSafeInteger(customerId) || customerId <= 0) {
      message.warning('员工工号必须为有效的正整数');
      return;
    }
    if (!customerName) {
      message.warning('请输入员工姓名');
      return;
    }
    const { customerInfo, remark } = employeeForm.value;
    const id = employee?.id;
    const customerType = employee?.customerType;
    employeeSaving.value = true;
    try {
      if (employee && (id == null || customerType == null)) {
        message.error('员工数据不完整，请刷新列表后重试');
        return;
      }
      const response =
        employee && id != null && customerType != null
          ? await editCustomer({
              id,
              customerType,
              customerInfo,
              remark,
            })
          : await addCustomer({ customerId, customerName, type: 1, customerInfo, remark });
      if (response.code !== 200) {
        message.error(response.msg || '员工保存失败');
        return;
      }
      if (employee && selectedEmployee.value?.customerId === employee.customerId) {
        selectedEmployee.value = { ...selectedEmployee.value, customerInfo, remark };
      }
      employeeFormOpen.value = false;
      message.success(employee ? '员工修改成功' : '员工新增成功');
      if (!employee) employeePage.value = 1;
      await loadEmployees();
    } catch (error) {
      console.error('员工保存失败:', error);
      message.error('员工保存失败，请稍后重试');
    } finally {
      employeeSaving.value = false;
    }
  };

  const confirmDeleteEmployee = (employee: CustomerModel): void => {
    if (deletingEmployeeId.value !== undefined) return;
    if (employee.id == null) {
      message.error('员工数据不完整，请刷新列表后重试');
      return;
    }
    const employeeId = employee.id;
    Modal.confirm({
      title: '确认删除员工？',
      content: `将删除员工“${employee.customerName}”（工号 ${employee.customerId}），请确认是否继续。`,
      okText: '确认删除',
      okType: 'danger',
      cancelText: '取消',
      async onOk(): Promise<void> {
        deletingEmployeeId.value = employeeId;
        try {
          const forbidResponse = await forbidCustomer({ id: employeeId, status: 1 });
          if (forbidResponse.code !== 200) {
            throw new Error(forbidResponse.msg || '员工禁用失败，未执行删除');
          }
          const response = await deleteCustomer({ id: employeeId });
          if (response.code !== 200) {
            throw new Error(response.msg || '员工已禁用，但删除失败');
          }
          if (selectedEmployee.value?.id === employeeId) {
            drawerOpen.value = false;
            selectedEmployee.value = undefined;
          }
          if (employees.value.length === 1 && employeePage.value > 1) {
            employeePage.value -= 1;
          }
          message.success('员工删除成功');
          await loadEmployees();
        } catch (error) {
          console.error('员工删除失败:', error);
          message.error(error instanceof Error ? error.message : '员工删除失败，请稍后重试');
          throw error;
        } finally {
          deletingEmployeeId.value = undefined;
        }
      },
    });
  };

  const handleSearch = (): void => {
    employeePage.value = 1;
    void loadEmployees();
  };

  const handleReset = (): void => {
    employeeIdInput.value = '';
    employeeNameInput.value = '';
    employeePage.value = 1;
    void loadEmployees();
  };

  const handleEmployeeTableChange = (pagination: TablePaginationConfig): void => {
    employeePage.value = pagination.current || 1;
    employeePageSize.value = pagination.pageSize || 10;
    void loadEmployees();
  };

  const fetchAllEmployees = async (): Promise<CustomerModel[]> => {
    const pageSize = 500;
    let pageNum = 1;
    let total = 0;
    const result: CustomerModel[] = [];
    do {
      const response = await getCustomerList({
        pageNum,
        pageSize,
        data: { type: 1, status: 0 },
      });
      if (response.code !== 200) throw new Error(response.msg || '员工数据查询失败');
      const pageRows = response.result?.list || [];
      result.push(...pageRows.filter((item) => item.disabled === 0));
      total = response.result?.total || 0;
      if (pageRows.length === 0) break;
      pageNum += 1;
    } while ((pageNum - 1) * pageSize < total);
    return result;
  };

  const exportAllEmployees = async (): Promise<void> => {
    exportLoading.value = true;
    try {
      const allEmployees = await fetchAllEmployees();
      if (allEmployees.length === 0) {
        message.warning('没有可导出的员工数据');
        return;
      }
      const worksheet = XLSX.utils.json_to_sheet(
        allEmployees.map((employee) => ({
          员工工号: String(employee.customerId),
          员工姓名: employee.customerName,
          餐券余额: employee.foodTicketAccount || 0,
          状态: employee.status === 0 && employee.disabled === 0 ? '正常' : '不可用',
        })),
      );
      worksheet['!cols'] = [{ wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 12 }];
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, '员工餐券');
      XLSX.writeFile(workbook, `员工餐券全量_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      message.success(`成功导出 ${allEmployees.length} 名员工`);
    } catch (error) {
      console.error('员工餐券导出失败:', error);
      message.error(error instanceof Error ? error.message : '员工餐券导出失败，请稍后重试');
    } finally {
      exportLoading.value = false;
    }
  };

  const downloadImportTemplate = (): void => {
    const worksheet = XLSX.utils.json_to_sheet([
      {
        员工工号: '10001',
        员工姓名: '张三',
        餐券数量: 10,
        调整原因: '示例：月度餐券发放',
      },
    ]);
    worksheet['!cols'] = [{ wch: 16 }, { wch: 18 }, { wch: 14 }, { wch: 30 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, '餐券调整导入');
    XLSX.writeFile(workbook, '员工餐券批量调整模板.xlsx');
  };

  const resetImportState = (): void => {
    importModalOpen.value = false;
    importFileName.value = '';
    importRows.value = [];
    importErrors.value = [];
    if (importFileInput.value) importFileInput.value.value = '';
  };

  const openImportFilePicker = (type: 'add' | 'reduce'): void => {
    resetImportState();
    importType.value = type;
    importFileInput.value?.click();
  };

  const validateImportRows = (
    rows: CouponImportExcelRow[],
    headers: string[],
    employeeMap: Map<number, CustomerModel>,
  ): CustomerAccountAdjustmentDetailModel[] => {
    const requiredHeaders = ['员工工号', '员工姓名', '餐券数量', '调整原因'];
    const errors = requiredHeaders
      .filter((header) => !headers.includes(header))
      .map((header) => `缺少必填列：${header}`);
    const result: CustomerAccountAdjustmentDetailModel[] = [];
    const employeeIds = new Set<number>();
    rows.forEach((row, index) => {
      const excelRow = index + 2;
      const rawId = row['员工工号'];
      const rawName = String(row['员工姓名'] ?? '').trim();
      const rawQuantity = row['餐券数量'];
      const rawRemark = String(row['调整原因'] ?? '').trim();
      const isBlank = [rawId, rawName, rawQuantity, rawRemark].every(
        (value) => value === undefined || value === null || value === '',
      );
      if (isBlank) return;
      const customerId = Number(rawId);
      const accountQuantity = Number(rawQuantity);
      const employee = employeeMap.get(customerId);
      let valid = true;
      if (!Number.isSafeInteger(customerId) || customerId <= 0) {
        errors.push(`第 ${excelRow} 行：员工工号必须为正整数`);
        valid = false;
      } else if (employeeIds.has(customerId)) {
        errors.push(`第 ${excelRow} 行：员工工号 ${customerId} 重复`);
        valid = false;
      } else if (!employee) {
        errors.push(`第 ${excelRow} 行：未找到正常状态的员工 ${customerId}`);
        valid = false;
      } else if (rawName !== employee.customerName) {
        errors.push(`第 ${excelRow} 行：员工姓名与工号 ${customerId} 不匹配`);
        valid = false;
      }
      if (!Number.isFinite(accountQuantity) || accountQuantity <= 0) {
        errors.push(`第 ${excelRow} 行：餐券数量必须为大于 0 的数字`);
        valid = false;
      } else if (
        importType.value === 'reduce' &&
        employee &&
        accountQuantity > employee.foodTicketAccount
      ) {
        errors.push(
          `第 ${excelRow} 行：扣减数量 ${accountQuantity} 超过当前余额 ${employee.foodTicketAccount}`,
        );
        valid = false;
      }
      if (!rawRemark) {
        errors.push(`第 ${excelRow} 行：调整原因不能为空`);
        valid = false;
      } else if (rawRemark.length > 200) {
        errors.push(`第 ${excelRow} 行：调整原因不能超过 200 个字符`);
        valid = false;
      }
      if (valid && employee) {
        employeeIds.add(customerId);
        result.push({
          customerId,
          customerName: employee.customerName,
          recordType: 1,
          accountQuantity,
          sourceRemark: rawRemark,
        });
      }
    });
    if (rows.length === 0 || (result.length === 0 && errors.length === 0)) {
      errors.push('Excel 中没有可导入的数据');
    }
    importErrors.value = errors;
    return result;
  };

  const handleImportFileChange = async (event: Event): Promise<void> => {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;
    importFileName.value = file.name;
    importModalOpen.value = true;
    importRows.value = [];
    importErrors.value = [];
    try {
      const [workbook, allEmployees] = await Promise.all([
        file.arrayBuffer().then((buffer) => XLSX.read(buffer, { type: 'array' })),
        fetchAllEmployees(),
      ]);
      const sheetName = workbook.SheetNames[0];
      if (!sheetName) {
        importErrors.value = ['Excel 中没有工作表'];
        return;
      }
      const sheet = workbook.Sheets[sheetName];
      const rows = XLSX.utils.sheet_to_json<CouponImportExcelRow>(sheet, { defval: '' });
      const headerRows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, range: 0 });
      const headers = (headerRows[0] || []).map((header) => String(header).trim());
      const employeeMap = new Map(allEmployees.map((employee) => [employee.customerId, employee]));
      importRows.value = validateImportRows(rows, headers, employeeMap);
    } catch (error) {
      console.error('解析员工餐券 Excel 失败:', error);
      importErrors.value = [
        error instanceof Error ? error.message : '文件无法解析，请确认文件是有效的 Excel 格式',
      ];
    }
  };

  const submitBatchImport = async (): Promise<void> => {
    if (importRows.value.length === 0 || importErrors.value.length > 0) return;
    importSubmitting.value = true;
    try {
      const request = { detailsList: importRows.value };
      const response =
        importType.value === 'add'
          ? await addCustomerFoodTicket(request)
          : await reduceCustomerFoodTicket(request);
      if (response.code !== 200) {
        message.error(response.msg || '批量调整餐券失败');
        return;
      }
      message.success(
        `成功${importType.value === 'add' ? '增加' : '扣减'} ${
          importRows.value.length
        } 名员工的餐券`,
      );
      resetImportState();
      await loadEmployees();
      if (selectedEmployee.value) {
        await refreshSelectedEmployee();
        recordPage.value = 1;
        await loadRecords();
      }
    } catch (error) {
      console.error('批量调整餐券失败:', error);
      message.error('批量调整餐券失败，请稍后重试');
    } finally {
      importSubmitting.value = false;
    }
  };

  const loadRecords = async (): Promise<void> => {
    if (!selectedEmployee.value) return;
    const request: CustomerAccountRecordSelectRequestModel = {
      pageNum: recordPage.value,
      pageSize: recordPageSize.value,
      createtimeBegin: recordDateRange.value?.[0]
        ? `${recordDateRange.value[0]} 00:00:00`
        : undefined,
      createtimeEnd: recordDateRange.value?.[1]
        ? `${recordDateRange.value[1]} 23:59:59`
        : undefined,
      data: {
        customerId: selectedEmployee.value.customerId,
        customerName: selectedEmployee.value.customerName,
        recordType: 1,
        direct: recordDirect.value,
      },
    };
    recordLoading.value = true;
    try {
      const response = await getCustomerAccountRecordList(request);
      if (response.code !== 200) {
        message.error(response.msg || '餐券流水查询失败');
        return;
      }
      records.value = response.result?.list || [];
      recordTotal.value = response.result?.total || 0;
    } catch (error) {
      console.error('餐券流水查询失败:', error);
      message.error('餐券流水查询失败，请稍后重试');
    } finally {
      recordLoading.value = false;
    }
  };

  const openEmployeeDetail = (employee: CustomerModel): void => {
    selectedEmployee.value = { ...employee };
    records.value = [];
    recordDateRange.value = undefined;
    recordDirect.value = undefined;
    recordPage.value = 1;
    drawerOpen.value = true;
    void loadRecords();
  };

  const searchRecords = (): void => {
    recordPage.value = 1;
    void loadRecords();
  };

  const resetRecordFilters = (): void => {
    recordDateRange.value = undefined;
    recordDirect.value = undefined;
    recordPage.value = 1;
    void loadRecords();
  };

  const handleRecordTableChange = (pagination: TablePaginationConfig): void => {
    recordPage.value = pagination.current || 1;
    recordPageSize.value = pagination.pageSize || 10;
    void loadRecords();
  };

  const openAdjustment = (type: 'add' | 'reduce'): void => {
    adjustmentType.value = type;
    adjustmentQuantity.value = undefined;
    adjustmentRemark.value = '';
    adjustmentOpen.value = true;
  };

  const refreshSelectedEmployee = async (): Promise<void> => {
    if (!selectedEmployee.value) return;
    const response = await getCustomerList({
      pageNum: 1,
      pageSize: 10,
      data: { type: 1, status: 0, customerId: selectedEmployee.value.customerId },
    });
    const refreshed = response.result?.list?.find(
      (item) => item.customerId === selectedEmployee.value?.customerId && item.disabled === 0,
    );
    if (response.code === 200 && refreshed) selectedEmployee.value = refreshed;
  };

  const executeAdjustment = async (): Promise<void> => {
    if (!selectedEmployee.value || !adjustmentQuantity.value) return;
    adjustmentLoading.value = true;
    try {
      const request = {
        detailsList: [
          {
            customerId: selectedEmployee.value.customerId,
            customerName: selectedEmployee.value.customerName,
            recordType: 1 as const,
            accountQuantity: adjustmentQuantity.value,
            sourceRemark: adjustmentRemark.value.trim(),
          },
        ],
      };
      const response =
        adjustmentType.value === 'add'
          ? await addCustomerFoodTicket(request)
          : await reduceCustomerFoodTicket(request);
      if (response.code !== 200) {
        message.error(response.msg || '餐券调整失败');
        return;
      }
      message.success(adjustmentType.value === 'add' ? '餐券增加成功' : '餐券扣减成功');
      adjustmentOpen.value = false;
      await Promise.all([refreshSelectedEmployee(), loadEmployees()]);
      recordPage.value = 1;
      await loadRecords();
    } catch (error) {
      console.error('餐券调整失败:', error);
      message.error('餐券调整失败，请稍后重试');
    } finally {
      adjustmentLoading.value = false;
    }
  };

  const submitAdjustment = (): void => {
    const quantity = adjustmentQuantity.value;
    const remark = adjustmentRemark.value.trim();
    if (!quantity || !Number.isFinite(quantity) || quantity <= 0) {
      message.warning('请输入大于 0 的有效数量');
      return;
    }
    if (
      adjustmentType.value === 'reduce' &&
      quantity > (selectedEmployee.value?.foodTicketAccount || 0)
    ) {
      message.warning('扣减数量不能超过当前餐券余额');
      return;
    }
    if (!remark) {
      message.warning('请输入调整原因');
      return;
    }
    Modal.confirm({
      title: adjustmentType.value === 'add' ? '确认增加餐券？' : '确认扣减餐券？',
      content: `${selectedEmployee.value?.customerName}（工号 ${
        selectedEmployee.value?.customerId
      }），${adjustmentType.value === 'add' ? '增加' : '扣减'} ${formatQuantity(
        quantity,
      )} 张。原因：${remark}`,
      okText: '确认',
      cancelText: '取消',
      onOk: executeAdjustment,
    });
  };

  onMounted(() => void loadEmployees());
</script>

<style scoped lang="less">
  .employee-coupon-manage {
    min-height: 100vh;
    padding: 0;
    background: #f0f2f5;

    .page-content {
      width: 100%;
    }

    .page-header {
      min-height: 62px;
      margin: 0 0 16px;
      padding: 10px 16px;
      background: #3b82f6;
      display: flex;
      align-items: center;
    }

    .page-back-btn {
      border: none;
      border-radius: 8px;
      background: #2563eb;
      color: #fff;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 8px 12px;
      margin-right: 16px;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }

    .page-back-btn:hover {
      background: #1d4ed8;
    }

    .page-title {
      color: #fff;
      font-size: 24px;
      font-weight: 600;
    }

    .search-form {
      margin-bottom: 16px;
      padding: 16px;
      background: #fff;
      border-radius: 4px;
      display: flex;
      flex-wrap: wrap;
      align-items: flex-end;
      gap: 16px;
    }

    .search-actions,
    .toolbar-actions {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
    }

    .query-field {
      width: 220px;
      flex: 0 0 220px;
    }

    .action-toolbar {
      margin-bottom: 16px;
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      justify-content: space-between;
      gap: 12px;
    }

    .toolbar-description {
      color: #8c8c8c;
      font-size: 14px;
    }

    .coupon-balance {
      color: #d97706;
      font-weight: 600;
    }

    :deep(.ant-card) {
      width: 100%;
      margin: 0;
      border-radius: 0;
      box-shadow: none;

      .ant-card-body {
        padding: 0;
      }
    }

    :deep(.ant-table-thead > tr > th) {
      background: #fafafa;
      font-weight: 600;
    }

    @media (max-width: 768px) {
      .page-header {
        min-height: 54px;
        margin-bottom: 12px;
      }

      .page-title {
        font-size: 20px;
      }

      .search-form {
        gap: 12px;
      }

      .query-field,
      .search-form :deep(.ant-input) {
        width: 100%;
      }

      .query-field {
        flex-basis: 100%;
      }

      .search-actions,
      .toolbar-actions {
        width: 100%;
      }

      .search-actions :deep(.ant-btn),
      .toolbar-actions :deep(.ant-btn) {
        flex: 1 1 auto;
      }
    }
  }
</style>
